import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Copy, Send, Trash2, Calendar, Clock, X } from 'lucide-react';

const SHIFT_COLORS = {
  morning: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20',
  standard: 'bg-gold/20 text-gold border-gold/20',
  evening: 'bg-blue-500/20 text-blue-400 border-blue-500/20',
  late_night: 'bg-purple-500/20 text-purple-400 border-purple-500/20',
  event: 'bg-pink-500/20 text-pink-400 border-pink-500/20',
  split: 'bg-amber-500/20 text-amber-400 border-amber-500/20',
};
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const roleColors = { manager: 'text-gold', director: 'text-purple-400', chef: 'text-amber-400', bartender: 'text-blue-400', shisha_expert: 'text-pink-400', waiter: 'text-emerald-400' };

function getMonday(dateStr) {
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  return mon.toISOString().split('T')[0];
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

export default function RosterPage() {
  const [weekStart, setWeekStart] = useState(getMonday());
  const [rosterData, setRosterData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [addShiftOpen, setAddShiftOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState({ user_id: '', date: '', start_time: '09:00', end_time: '17:00', shift_type: 'standard', notes: '' });
  const [templateDialog, setTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', shift_type: 'standard', start_time: '09:00', end_time: '17:00', break_minutes: 30, roles: [], color: '#C7871F' });
  const [saving, setSaving] = useState(false);

  const fetchRoster = useCallback(async () => {
    try { const { data } = await api.get(`/staff/roster-view?week_start=${weekStart}`); setRosterData(data); } catch (err) { console.error('[RosterPage.js] error:', err); }
  }, [weekStart]);

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await api.get('/staff/templates'); setTemplates(data); } catch (err) { console.error('[RosterPage.js] error:', err); }
  }, []);

  useEffect(() => { fetchRoster(); fetchTemplates(); }, [fetchRoster, fetchTemplates]);

  const dates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));

  const openAddShift = (userId, date) => {
    setShiftForm({ user_id: userId, date, start_time: '09:00', end_time: '17:00', shift_type: 'standard', notes: '' });
    setAddShiftOpen(true);
  };

  const applyTemplate = (template) => {
    setShiftForm(f => ({ ...f, start_time: template.start_time, end_time: template.end_time, shift_type: template.shift_type }));
  };

  const createShift = async () => {
    setSaving(true);
    try {
      await api.post('/staff/shifts', shiftForm);
      toast.success('Shift created');
      setAddShiftOpen(false);
      fetchRoster();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteShift = async (shiftId) => {
    try { await api.delete(`/staff/shifts/${shiftId}`); toast.success('Shift removed'); fetchRoster(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyWeek = async () => {
    const targetWeek = addDays(weekStart, 7);
    try {
      const { data } = await api.post('/staff/shifts/repeat', { source_week: weekStart, target_week: targetWeek });
      toast.success(data.message);
      setWeekStart(targetWeek);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createTemplate = async () => {
    try {
      await api.post('/staff/templates', templateForm);
      toast.success('Template created');
      setTemplateDialog(false);
      setTemplateForm({ name: '', shift_type: 'standard', start_time: '09:00', end_time: '17:00', break_minutes: 30, roles: [], color: '#C7871F' });
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/staff/templates/${id}`); toast.success('Template deleted'); fetchTemplates(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const publishRoster = async () => {
    try {
      const { data: roster } = await api.post('/staff/rosters', { name: `Week ${formatDate(weekStart)}`, week_start: weekStart, roster_type: 'weekly' });
      await api.put(`/staff/rosters/${roster.id}/publish`);
      toast.success('Roster published! All staff notified.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={prevWeek} className="h-8 w-8 p-0 text-zinc-400 hover:text-white" data-testid="roster-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-center min-w-[200px]">
            <p className="text-white font-sans text-sm font-medium">{formatDate(weekStart)} — {formatDate(addDays(weekStart, 6))}</p>
            <p className="text-zinc-500 font-sans text-xs">Week of {weekStart}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={nextWeek} className="h-8 w-8 p-0 text-zinc-400 hover:text-white" data-testid="roster-next-week"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setTemplateDialog(true)} className="h-8 text-zinc-400 hover:text-white font-sans text-xs border border-white/10" data-testid="manage-templates">
            <Clock className="w-3 h-3 mr-1" /> Templates
          </Button>
          <Button size="sm" variant="ghost" onClick={copyWeek} className="h-8 text-zinc-400 hover:text-white font-sans text-xs border border-white/10" data-testid="copy-week">
            <Copy className="w-3 h-3 mr-1" /> Copy to Next Week
          </Button>
          <Button size="sm" onClick={publishRoster} className="h-8 bg-gold text-black font-bold font-sans text-xs rounded-sm" data-testid="publish-roster">
            <Send className="w-3 h-3 mr-1" /> Publish
          </Button>
        </div>
      </div>

      {/* Roster Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Day Headers */}
          <div className="grid grid-cols-[180px_repeat(7,1fr)] gap-1 mb-1">
            <div className="p-2 text-xs font-sans text-zinc-600 uppercase tracking-wider">Staff</div>
            {dates.map((d, i) => {
              const isToday = d === new Date().toISOString().split('T')[0];
              return (
                <div key={d} className={`p-2 text-center rounded-sm ${isToday ? 'bg-gold/10 border border-gold/20' : 'bg-white/[0.02]'}`}>
                  <p className={`text-xs font-sans font-bold ${isToday ? 'text-gold' : 'text-zinc-400'}`}>{DAYS[i]}</p>
                  <p className={`text-[10px] font-sans ${isToday ? 'text-gold/70' : 'text-zinc-600'}`}>{formatDate(d)}</p>
                </div>
              );
            })}
          </div>

          {/* Staff Rows */}
          {rosterData?.staff?.map(staff => (
            <div key={staff.user_id} className="grid grid-cols-[180px_repeat(7,1fr)] gap-1 mb-1" data-testid={`roster-row-${staff.user_id}`}>
              <div className="p-2 bg-white/[0.02] rounded-sm flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-xs font-sans font-bold text-zinc-400">
                  {staff.user_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-sans text-xs truncate">{staff.user_name}</p>
                  <p className={`text-[10px] font-sans capitalize ${roleColors[staff.role] || 'text-zinc-500'}`}>{staff.role}</p>
                </div>
              </div>
              {dates.map(d => {
                const shift = staff.shifts[d];
                return (
                  <div key={d} className="min-h-[56px] bg-white/[0.01] border border-white/[0.03] rounded-sm p-1 relative group">
                    {shift ? (
                      <div className={`h-full rounded-sm border p-1.5 ${SHIFT_COLORS[shift.shift_type] || SHIFT_COLORS.standard}`} data-testid={`shift-${shift.id}`}>
                        <p className="text-[11px] font-sans font-medium">{shift.start_time}-{shift.end_time}</p>
                        <p className="text-[9px] font-sans opacity-70 capitalize">{shift.shift_type}</p>
                        <button onClick={() => deleteShift(shift.id)} className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300" data-testid={`delete-shift-${shift.id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => openAddShift(staff.user_id, d)} className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-zinc-600 hover:text-gold" data-testid={`add-shift-${staff.user_id}-${d}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Add Shift Dialog */}
      <Dialog open={addShiftOpen} onOpenChange={setAddShiftOpen}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Add Shift</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-zinc-400 font-sans text-xs">Date: <span className="text-white">{shiftForm.date && formatDate(shiftForm.date)}</span></p>
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Quick Apply Template</Label>
                <div className="flex flex-wrap gap-1">
                  {templates.map(t => (
                    <Button key={t.id} size="sm" variant="ghost" onClick={() => applyTemplate(t)} className="h-7 text-xs font-sans border border-white/10 text-zinc-300 hover:text-gold" data-testid={`apply-template-${t.id}`}>
                      {t.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Start</Label>
                <Input type="time" value={shiftForm.start_time} onChange={e => setShiftForm(p => ({ ...p, start_time: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="shift-start-time" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">End</Label>
                <Input type="time" value={shiftForm.end_time} onChange={e => setShiftForm(p => ({ ...p, end_time: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="shift-end-time" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Shift Type</Label>
              <Select value={shiftForm.shift_type} onValueChange={v => setShiftForm(p => ({ ...p, shift_type: v }))}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="shift-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">
                  {Object.keys(SHIFT_COLORS).map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={shiftForm.notes} onChange={e => setShiftForm(p => ({ ...p, notes: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" placeholder="Notes (optional)" data-testid="shift-notes" />
            <Button onClick={createShift} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-shift">
              {saving ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Add Shift'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Management Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-white">Shift Templates</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {templates.length > 0 && (
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`template-${t.id}`}>
                    <div>
                      <p className="text-white font-sans text-sm">{t.name}</p>
                      <p className="text-zinc-500 font-sans text-xs">{t.start_time} - {t.end_time} ({t.shift_type})</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.id)} className="h-7 text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-white/5 pt-3">
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans mb-3">New Template</p>
              <div className="space-y-3">
                <Input value={templateForm.name} onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" placeholder="Template name" data-testid="template-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="time" value={templateForm.start_time} onChange={e => setTemplateForm(p => ({ ...p, start_time: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="template-start" />
                  <Input type="time" value={templateForm.end_time} onChange={e => setTemplateForm(p => ({ ...p, end_time: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="template-end" />
                </div>
                <Select value={templateForm.shift_type} onValueChange={v => setTemplateForm(p => ({ ...p, shift_type: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    {Object.keys(SHIFT_COLORS).map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={createTemplate} disabled={!templateForm.name} className="w-full bg-gold text-black font-bold rounded-sm font-sans text-sm" data-testid="save-template">Create Template</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
